/**
 * Universal product activity — the CLIENT side.
 *
 * The backend endpoint accepting a product key is not instrumentation. Something has to actually
 * say "this customer opened this product", and this is that something.
 *
 * What it does NOT do:
 *   - it never sends a user id. Identity comes from the authenticated session on the server, and a
 *     payload identity would be a client claiming to be someone;
 *   - it never sends filenames, document content, generated content or anything the customer made;
 *   - it never sends a timestamp — the server clock decides when something happened;
 *   - it never blocks or breaks the page. Telemetry that can take a product down is worse than no
 *     telemetry, so every failure is swallowed.
 *
 * The taxonomy is three values and stays three values. Product-specific richness belongs in that
 * product's own domain tables, not here.
 */
import api from './api';

export const PRODUCT_OPENED = 'OPENED';
export const PRODUCT_OPERATION_COMPLETED = 'OPERATION_COMPLETED';
export const PRODUCT_OPERATION_FAILED = 'OPERATION_FAILED';

// One OPENED per product per browser session. Re-mounting a page — a route change, a React strict
// double-render, a back button — is not a second use, and counting it as one would inflate every
// figure the owner reads.
const SESSION_PREFIX = 'vi_product_opened:';

function sessionKeyFor(productKey) {
  return `${SESSION_PREFIX}${productKey}`;
}

function alreadyOpenedThisSession(productKey) {
  try {
    return window.sessionStorage.getItem(sessionKeyFor(productKey)) !== null;
  } catch (e) {
    return false;
  }
}

function markOpenedThisSession(productKey, token) {
  try {
    window.sessionStorage.setItem(sessionKeyFor(productKey), token);
  } catch (e) {
    /* private mode, storage disabled — the server dedupe key still bounds retries */
  }
}

function dedupeToken(productKey) {
  // Stable for the browser session, so a retry after a network failure is the SAME delivery rather
  // than a second use. The server enforces this with a UNIQUE index; this only avoids the round trip.
  try {
    const existing = window.sessionStorage.getItem(sessionKeyFor(productKey));
    if (existing) return existing;
  } catch (e) { /* fall through */ }
  const day = new Date().toISOString().slice(0, 10);
  const rand = Math.random().toString(36).slice(2, 10);
  return `${productKey}:${day}:${rand}`;
}

/**
 * Record one meaningful product action. Fire-and-forget by design.
 *
 * @param {string} productKey canonical VISINVOKE product key — validated server-side
 * @param {object} options    {eventType, source, outcome, dedupeKey, once}
 */
export function emitProductActivity(productKey, options = {}) {
  if (!productKey) return Promise.resolve(null);

  const eventType = options.eventType || PRODUCT_OPENED;
  const once = options.once !== undefined ? options.once : eventType === PRODUCT_OPENED;

  if (once && alreadyOpenedThisSession(productKey)) return Promise.resolve(null);

  const dedupeKey = options.dedupeKey || dedupeToken(productKey);
  if (once) markOpenedThisSession(productKey, dedupeKey);

  // Wrapped, not chained directly. `.catch` only protects a rejected promise - a transport that
  // returns nothing at all, or throws synchronously, would still take the page down, and telemetry
  // must NEVER break a product. Promise.resolve() makes every one of those the same harmless null.
  try {
    return Promise.resolve(
      api.post('/v1/products/activity', {
        product_key: productKey,
        event_type: eventType,
        source: options.source || 'web',
        // A coarse class only. Never a message, never a stack trace, never a filename.
        outcome: options.outcome || undefined,
        dedupe_key: dedupeKey,
      }),
    )
      .then((response) => (response ? response.data : null))
      .catch(() => null);
  } catch (e) {
    return Promise.resolve(null);
  }
}

/**
 * Emit OPENED once when a product surface mounts.
 *
 * Written as a plain function rather than a hook so it can be called from a `useEffect` in any page
 * without adding a dependency-array trap; pages call it with an empty dependency list.
 */
export function recordProductOpened(productKey, source) {
  return emitProductActivity(productKey, { eventType: PRODUCT_OPENED, source, once: true });
}

/**
 * Emit the outcome of one meaningful operation.
 *
 * `outcome` is a short class such as 'ok', 'invalid_input' or 'provider_error'. It must never carry
 * what the customer was working on.
 */
export function recordProductOperation(productKey, succeeded, outcome, source) {
  return emitProductActivity(productKey, {
    eventType: succeeded ? PRODUCT_OPERATION_COMPLETED : PRODUCT_OPERATION_FAILED,
    outcome: outcome ? String(outcome).slice(0, 32) : undefined,
    source,
    once: false,
  });
}

export default emitProductActivity;
